/**
 * Fachliche Typen des Drumpattern-Modells.
 *
 * Diese Datei enthaelt ausschliesslich das musikalische Datenmodell.
 * Sie darf nicht von HTML, DOM oder UI-Belangen abhaengen.
 */

/**
 * Unterstuetzte Schlaginstrumente.
 * Als `const` Objekt plus abgeleitetem Union-Typ, damit die Werte
 * sowohl zur Laufzeit (Iteration, Validierung) als auch zur
 * Compile-Zeit (Typpruefung) verfuegbar sind.
 */
export const DrumInstrument = {
  Crash: 'crash',
  Ride: 'ride',
  HiHat: 'hihat',
  HighTom: 'hightom',
  MidTom: 'midtom',
  Snare: 'snare',
  FloorTom: 'floortom',
  Bassdrum: 'bassdrum',
} as const;

export type DrumInstrument =
  (typeof DrumInstrument)[keyof typeof DrumInstrument];

/**
 * Reihenfolge der Instrumente, wie sie im Editor angezeigt werden
 * (von oben nach unten). Die Toms sind zwischen die vorhandenen Stimmen
 * einsortiert, sodass die vertikale Anordnung der ueblichen
 * Drumset-Notation folgt (hohe Toms oben, Floor Tom unter der Snare).
 */
export const INSTRUMENT_ORDER: readonly DrumInstrument[] = [
  DrumInstrument.Crash,
  DrumInstrument.Ride,
  DrumInstrument.HiHat,
  DrumInstrument.HighTom,
  DrumInstrument.MidTom,
  DrumInstrument.Snare,
  DrumInstrument.FloorTom,
  DrumInstrument.Bassdrum,
];

/**
 * Menschlich lesbare Bezeichnungen der Instrumente.
 */
export const INSTRUMENT_LABELS: Readonly<Record<DrumInstrument, string>> = {
  [DrumInstrument.Crash]: 'Crash',
  [DrumInstrument.Ride]: 'Ride',
  [DrumInstrument.HiHat]: 'Hi-Hat',
  [DrumInstrument.HighTom]: 'High Tom',
  [DrumInstrument.MidTom]: 'Mid Tom',
  [DrumInstrument.Snare]: 'Snare',
  [DrumInstrument.FloorTom]: 'Floor Tom',
  [DrumInstrument.Bassdrum]: 'Bassdrum',
};

/**
 * Anzahl der zeitlichen Schritte in einem Takt (4/4, Sechzehntelraster).
 */
export const STEP_COUNT = 16;

/**
 * Zeitposition in Sechzehntel-Einheiten, 0 <= step < 16.
 * Triolenpositionen liegen auf Dritteln; gespeichert werden ganzzahlige Ticks.
 * Der Wertebereich wird ueber {@link isStepIndex} bzw.
 * {@link assertStepIndex} durchgesetzt.
 */
export type StepIndex = number;

/**
 * Art eines Schlags. In DP-1 existiert ausschliesslich der normale Schlag.
 */
export const HitType = {
  Normal: 'NORMAL',
} as const;

export type HitType = (typeof HitType)[keyof typeof HitType];

/**
 * Ein einzelner Schlag auf einem Rasterfeld.
 */
export interface DrumHit {
  readonly type: HitType;
}

/**
 * Prueft, ob ein Wert ein bekanntes Instrument ist.
 */
export function isDrumInstrument(value: unknown): value is DrumInstrument {
  return (
    typeof value === 'string' &&
    (INSTRUMENT_ORDER as readonly string[]).includes(value)
  );
}

/**
 * Prueft, ob ein Wert ein gueltige Zeitposition (0 <= step < 16, Vielfaches von 1/3) ist.
 */
export function isStepIndex(value: unknown): value is StepIndex {
  return (
    typeof value === 'number' &&
    Number.isInteger(value * 3) &&
    value >= 0 &&
    value < STEP_COUNT
  );
}

/**
 * Prueft, ob ein Wert eine bekannte HitType-Auspraegung ist.
 */
export function isHitType(value: unknown): value is HitType {
  return value === HitType.Normal;
}

/** Optionale Hand- und Fußzuordnung pro Rasterposition. */
export const HAND_VALUES = ['', 'R', 'L', 'RL'] as const;
export const FOOT_VALUES = ['', 'K', 'H', 'KH'] as const;
export type Hand = (typeof HAND_VALUES)[number];
export type Foot = (typeof FOOT_VALUES)[number];
export type Sticking =
  Hand | Foot | `${Exclude<Hand, ''>}+${Exclude<Foot, ''>}`;
export const STICKING_VALUES: readonly Sticking[] = HAND_VALUES.flatMap(
  (hand) => FOOT_VALUES.map((foot) => combineSticking(hand, foot)),
);
export function combineSticking(hand: Hand, foot: Foot): Sticking {
  return hand && foot ? `${hand}+${foot}` : hand || foot;
}
export function splitSticking(value: Sticking): { hand: Hand; foot: Foot } {
  const [first, second = ''] = value.split('+');
  return (HAND_VALUES as readonly string[]).includes(first!)
    ? { hand: first as Hand, foot: second as Foot }
    : { hand: '', foot: first as Foot };
}
export function isSticking(value: unknown): value is Sticking {
  return (
    typeof value === 'string' &&
    (STICKING_VALUES as readonly string[]).includes(value)
  );
}

/** Unterteilungen je Viertel; Zeitpositionen bleiben in Sechzehntel-Einheiten.
 * Intern entsprechen drei Ticks einem Sechzehntel (12 Ticks je Viertel). */
export const SUBDIVISIONS = [2, 3, 4, 6] as const;
export type Subdivision = (typeof SUBDIVISIONS)[number];
export function isSubdivision(value: unknown): value is Subdivision {
  return SUBDIVISIONS.some((item) => item === value);
}
export function stepsForBeat(beat: number, subdivision: Subdivision): number[] {
  return Array.from(
    { length: subdivision },
    (_, slot) => (beat * 12 + slot * (12 / subdivision)) / 3,
  );
}
